import React, { useState } from 'react';
import { Settings, Filter, FileText, Clock, Bell, Layout, CheckSquare, MessageCircle, Wallet, Landmark, ScrollText } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import SidebarLayout from '../../Layouts/SidebarLayout';
import { useSystemConfiguration } from './hooks/useSystemConfiguration';
import { CONFIGURATION_CATEGORIES } from './types';
import SubtramiteSelector from './components/SubtramiteSelector';
import WhatsAppSettingsForm from './components/WhatsAppSettingsForm';
import ContabilidadSettingsForm from './components/ContabilidadSettingsForm';
import DestinosPagoSettings from './components/DestinosPagoSettings';
import { EditorLeyendas } from '@/presupuestos/components/EditorLeyendas';
import { leyendasValidas } from '@/presupuestos/utils/leyendas';

const SystemConfiguration: React.FC = () => {
  const {
    configuration,
    isLoading,
    isError,
    error,
    activeTab,
    setActiveTab,
    hasChanges,
    saveChanges,
    discardChanges,
    resetToDefaults,
    isSaving,
    updateSubtramitesConfiguration,
    updateWhatsAppConfiguration,
    updateContabilidadConfiguration,
    updatePresupuestosConfiguration,
  } = useSystemConfiguration();
  const [whatsAppValidationError, setWhatsAppValidationError] = useState<string | null>(null);
  const [contabilidadValidationError, setContabilidadValidationError] = useState<string | null>(null);
  const leyendasInvalidas = !leyendasValidas(configuration?.presupuestos_settings?.notas ?? []);

  const getTabIcon = (category: string) => {
    switch (category) {
      case 'subtramites': return CheckSquare;
      case 'filters': return Filter;
      case 'files': return FileText;
      case 'session': return Clock;
      case 'notifications': return Bell;
      case 'interface': return Layout;
      case 'whatsapp': return MessageCircle;
      case 'contabilidad': return Wallet;
      case 'presupuestos': return Landmark;
      default: return Settings;
    }
  };

  if (isLoading) {
    return (
      <SidebarLayout title="Configuración del Sistema">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Cargando configuración...</p>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  if (isError) {
    return (
      <SidebarLayout title="Configuración del Sistema">
        <div className="p-2 sm:p-6 max-w-4xl mx-auto">
          <Alert className="border-red-200 bg-red-50">
            <AlertDescription className="text-red-800">
              <div className="space-y-2">
                <p className="font-medium">No se pudo cargar la configuración del sistema</p>
                <p className="text-sm">
                  El endpoint de configuración no está disponible en el backend.
                  Implementa el endpoint <code className="bg-red-100 px-1 rounded">/system-configuration</code> para habilitar esta funcionalidad.
                </p>
                <p className="text-xs text-red-600">
                  Error: {error?.message || 'Endpoint no encontrado'}
                </p>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout title="Configuración del Sistema">
      <div className="flex flex-col h-full">
        {/* Header with save actions */}
        {hasChanges && (
          <div className="border-b bg-yellow-50 px-3 sm:px-6 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                  Cambios pendientes
                </Badge>
                <span className="text-sm text-gray-600">
                  Tienes cambios sin guardar en la configuración
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={discardChanges}
                  disabled={isSaving}
                >
                  Descartar
                </Button>
                <Button
                  size="sm"
                  onClick={saveChanges}
                  disabled={isSaving || !!whatsAppValidationError || !!contabilidadValidationError || leyendasInvalidas}
                >
                  {isSaving ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 p-3 sm:p-6">
          <div className="max-w-6xl mx-auto">
            {/* Page header */}
            <div className="mb-8">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Configuración del Sistema
                  </h1>
                  <p className="text-gray-600 mt-1">
                    Administra los parámetros y configuraciones globales del sistema
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={resetToDefaults}
                  disabled={isSaving}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Restablecer por defecto
                </Button>
              </div>
            </div>

            {/* Configuration tabs */}
            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
              <TabsList
                className="grid w-full"
                style={{ gridTemplateColumns: `repeat(${CONFIGURATION_CATEGORIES.length}, minmax(0, 1fr))` }}
              >
                {CONFIGURATION_CATEGORIES.map((category) => {
                  const Icon = getTabIcon(category.key);
                  return (
                    <TabsTrigger key={category.key} value={category.key} className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      <span className="hidden sm:inline">{category.name}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {/* Subtramites Configuration */}
              <TabsContent value="subtramites">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckSquare className="h-5 w-5" />
                      Configuración de Subtramites
                    </CardTitle>
                    <CardDescription>
                      Selecciona qué subtramites se iniciarán automáticamente al crear un nuevo trámite
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {configuration ? (
                      <SubtramiteSelector
                        selectedSubtramites={configuration.subtramites_configuration?.default_enabled || []}
                        onChange={() => {
                          // This will be handled by the SubtramiteSelector component
                        }}
                        updateSubtramitesConfiguration={updateSubtramitesConfiguration}
                      />
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                        <p className="mt-2">Cargando configuración...</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="whatsapp">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageCircle className="h-5 w-5" />
                      Configuración de WhatsApp
                    </CardTitle>
                    <CardDescription>
                      Ajusta límites de envíos, idempotencia y parámetros de enlaces temporales.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {configuration?.whatsapp_settings ? (
                      <WhatsAppSettingsForm
                        settings={configuration.whatsapp_settings}
                        onChange={updateWhatsAppConfiguration}
                        onValidationChange={setWhatsAppValidationError}
                      />
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                        <p className="mt-2">Cargando configuración de WhatsApp...</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="contabilidad">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Wallet className="h-5 w-5" />
                      Configuración de Contabilidad
                    </CardTitle>
                    <CardDescription>
                      Define desde qué fecha se listan los pendientes y qué otros trámites van al fondo Certificaciones y poderes.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {configuration?.contabilidad_settings ? (
                      <ContabilidadSettingsForm
                        settings={configuration.contabilidad_settings}
                        onChange={updateContabilidadConfiguration}
                        onValidationChange={setContabilidadValidationError}
                      />
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                        <p className="mt-2">Cargando configuración de contabilidad...</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="presupuestos" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Landmark className="h-5 w-5" />
                      Destinos de pago
                    </CardTitle>
                    <CardDescription>
                      Cuentas a las que se deposita el dinero de cada ítem de presupuesto. Se imprimen
                      en el PDF con el monto a depositar. Los cambios se guardan al momento.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DestinosPagoSettings />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ScrollText className="h-5 w-5" />
                      Leyendas del PDF
                    </CardTitle>
                    <CardDescription>
                      Se imprimen numeradas al pie de los PDF. Cada presupuesto nuevo recibe una copia
                      que se puede ajustar desde el presupuesto; los existentes conservan las suyas.
                      Se guardan con "Guardar Cambios".
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {configuration?.presupuestos_settings ? (
                      <EditorLeyendas
                        leyendas={configuration.presupuestos_settings.notas}
                        onChange={(notas) => updatePresupuestosConfiguration({ notas })}
                        deshabilitado={isSaving}
                      />
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                        <p className="mt-2">Cargando leyendas...</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </SidebarLayout>
  );
};

export default SystemConfiguration;
